import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { RootState } from "src/redux/reducers";
import { getRoute, ROUTES } from "../../utils/ROUTES";
import { setChatwootUnreadMessages } from "src/redux/main/mainActions";
import { API_URLS } from "../../utils/API_URLS";

interface Props {
  children: ReactNode;
}

declare global {
  interface Window {
    chatwootSettings: any;
    chatwootSDK: any;
    $chatwoot: any;
  }
}

const ChatwootProvider = ({ children }: Props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const { identity, activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  useEffect(() => {
    const onChatwootReady = async () => {
      setIsReady(true);
      await fetch("https://help.indahash.com/identify", {
        credentials: "include",
      });

      // Inject custom styles after Chatwoot loads
      // Use setTimeout to ensure our styles come after Chatwoot's inline styles
      setTimeout(() => {
        const styleId = "chatwoot-custom-styles";
        if (!document.getElementById(styleId)) {
          const style = document.createElement("style");
          style.id = styleId;
          style.textContent = `
            @media only screen and (min-width: 667px) {
              .woot-widget-holder {
                max-height: 720px !important;
                width: 600px !important;
              }
            }
          `;
          document.body.appendChild(style);
        }
      }, 100);
    };

    window.addEventListener("chatwoot:ready", onChatwootReady);
    return () => {
      window.removeEventListener("chatwoot:ready", onChatwootReady);
    };
  }, []);

  useEffect(() => {
    if (!isReady) return;

    const onMessage = (e: any) => {
      if (window.$chatwoot) {
        window.$chatwoot.setConversationCustomAttributes({
          active_url: window.location.href,
        });
      }

      const count = e?.detail?.count ?? 0;
      dispatch(setChatwootUnreadMessages(count));
    };

    window.addEventListener("chatwoot:on-message", onMessage);
    return () => {
      window.removeEventListener("chatwoot:on-message", onMessage);
    };
  }, [isReady, dispatch]);

  useEffect(() => {
    const websiteToken = identity?.chatwootId?.token;
    if (!websiteToken || window.chatwootSDK) {
      return;
    }

    // ALWAYS clear the cw_conversation cookie before loading the widget
    // This ensures the widget loads fresh and subscribes to WebSocket with correct identity
    // The cookie may be stale from a different workspace/user session
    document.cookie =
      "cw_conversation=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

    window.chatwootSettings = {
      position: "left",
      type: "standard",
      enableFileUpload: true, // Show file attachment button
      enableEmojiPicker: true, // Enable emoji picker in the chat input
      enableEndConversation: false, // Let users end the conversation
    };

    (function (d, t) {
      const BASE_URL = "https://support.idh.cloud";
      const g: any = d.createElement(t);
      const s: any = d.getElementsByTagName(t)[0];
      g.src = BASE_URL + "/packs/js/sdk.js";
      g.defer = true;
      g.async = true;
      if (s && s.parentNode) {
        s.parentNode.insertBefore(g, s);
      } else {
        d.head.appendChild(g);
      }
      g.onload = function () {
        window.chatwootSDK.run({
          websiteToken,
          baseUrl: BASE_URL,
        });
      };
    })(document, "script");
  }, [identity?.chatwootId?.token]);

  useEffect(() => {
    if (
      isReady &&
      identity?.id &&
      identity?.email &&
      identity?.chatwootId?.id &&
      identity?.chatwootId?.hash &&
      identity?.chatwootId?.token &&
      activeWorkspaceUuid &&
      window.$chatwoot
    ) {
      const workspaceUuid = identity.chatwootId.id.split(":")[0];
      const memberUuid = identity.chatwootId.id.split(":")[1];

      const workspaceLink =
        window.location.origin +
        getRoute(ROUTES.HOME, {
          wsWorkspaceUuid: workspaceUuid,
        });

      window.$chatwoot.setUser(identity.chatwootId.id, {
        email: identity.email,
        identifier_hash: identity.chatwootId.hash,
        name: identity?.name,
        avatar_url: identity?.avatarUrl,
      });

      window.$chatwoot.setCustomAttributes({
        workspace_url: workspaceLink,
        workspace_uuid: workspaceUuid,
        user_system_id: memberUuid,
        user_system_email_address: identity.email,
        workspace_name: identity.wsWorkspace?.name,
        is_workspace_hostname_customized:
          identity.isWorkspaceHostnameCustomized,
      });

      window.$chatwoot.setConversationCustomAttributes({
        active_url: window.location.href,
      });

      try {
        fetch(
          `${API_URLS.chatwootLoaded}?wsWorkspaceUuid=${activeWorkspaceUuid}`,
        );
      } catch (error) {
        // silence
      }
    }
  }, [isReady, identity, activeWorkspaceUuid]);

  useEffect(() => {
    if (isReady && window.$chatwoot) {
      window.$chatwoot.setConversationCustomAttributes({
        active_url: window.location.href,
      });
    }
  }, [isReady, location]);

  return <>{children}</>;
};

export default ChatwootProvider;
